import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
} from "antd";
import { useTranslation } from "react-i18next";

import { useUpdateTreuhandstelleMutation } from "store/masterdata";
import { UpdateTHsParams } from "types/masterdata.types";
import TextArea from "antd/lib/input/TextArea";
import { useLazyGetEnumInfoQuery } from "store/system";
import { isValid } from "iban";

interface Props {
  initialValues: UpdateTHsParams;
  visible: boolean;
  onClose(): void;
  onSuccess(): void;
}

function UpdateTHsForm({
  initialValues,
  visible,
  onClose,
  onSuccess,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const [tHsForm] = Form.useForm();

  const [updateTHs, updateTHsResult] = useUpdateTreuhandstelleMutation();
  const [getEnumInfo] = useLazyGetEnumInfoQuery();
  const handleTHsFormSubmit = (values: UpdateTHsParams) => {
    const { ...params } = values;
    updateTHs(params);
  };
  const validateIban = (_: unknown, value: string) => {
    if (!value || isValid(value)) {
      return Promise.resolve();
    }
    return Promise.reject(new Error("Invalid IBAN"));
  };

  const [ibanOptionValues, setIbanOptionValues] = useState<
    Record<string, number>
  >({});

  const langOptions = [0, 1, 2].map((option) => ({
    label: t(`masterdata.enums.LanguageEnum.${option}`),
    value: option,
  }));

  const getEnum = async (
    name: string,
    setter: React.Dispatch<React.SetStateAction<Record<string, number>>>
  ) => {
    try {
      const payload = await getEnumInfo({
        enumName: name,
      }).unwrap();
      if (payload) {
        setter(payload);
      }
    } catch (error) {
      console.error("GetEnumInfo Error", error);
    }
  };

  useEffect(() => {
    if (updateTHsResult.isSuccess && !updateTHsResult.data?.hasErrors) {
      onSuccess();
      onClose();
    }
  }, [updateTHsResult]);

  useEffect(() => {
    getEnum("IbanOptionsEnum", setIbanOptionValues);
  }, []);

  return (
    <Modal
      footer={null}
      title={t("masterdata.lw_details.edit_ths_infos")}
      visible={visible}
      onCancel={onClose}
      destroyOnClose
    >
      <Form
        id="ths-form"
        form={tHsForm}
        onFinish={handleTHsFormSubmit}
        layout="vertical"
        initialValues={initialValues}
      >
        {updateTHsResult.data?.hasErrors && (
          <>
            <Alert
              message={updateTHsResult.data?.errorDetails.errorMessage}
              type="error"
            />
            <Divider />
          </>
        )}
        <Row gutter={20}>
          <Col span={12}>
            <Form.Item name="id" hidden>
              <Input />
            </Form.Item>
            <Form.Item
              label={t("masterdata.lw_details.ths_number")}
              name="extId"
            >
              <Input disabled />
            </Form.Item>
            <Form.Item label={t("masterdata.lw_details.name")} name="name">
              <Input />
            </Form.Item>
            <Form.Item
              label={t("masterdata.lw_details.email")}
              name="email"
              rules={[{ type: "email" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item label={t("masterdata.lw_details.phone")} name="phone">
              <Input />
            </Form.Item>
            <Form.Item
              name="language"
              label={t("masterdata.lw_details.language")}
              rules={[{ required: true }]}
            >
              <Select options={langOptions} />
            </Form.Item>
            <Form.Item
              label={t("masterdata.lw_details.adresse")}
              name="address"
            >
              <Input />
            </Form.Item>
            <Form.Item label={t("masterdata.lw_details.ort")} name="place">
              <Input />
            </Form.Item>
            <Form.Item
              label={t("masterdata.lw_details.postal_code")}
              name="postalCode"
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t("masterdata.lw_details.cati_comment")}
              name="catiComment"
            >
              <TextArea />
            </Form.Item>
          </Col>
          <Col span={12}>
            <p>{t("IBAN-Informationen")}</p>
            <Divider />
            <Form.Item
              label={t("masterdata.lw_details.iban")}
              name="iban"
              rules={[{ validator: validateIban }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t("masterdata.lw_details.ibanName")}
              name="ibanName"
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t("masterdata.lw_details.ibanAddress")}
              name="ibanAddress"
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t("masterdata.lw_details.ibanZipCode")}
              name="ibanZipCode"
              normalize={(value) => (value === "" ? undefined : value)}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t("masterdata.lw_details.ibanTown")}
              name="ibanTown"
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t("masterdata.lw_details.ibanComment")}
              name="ibanComment"
            >
              <TextArea />
            </Form.Item>
            <Form.Item
              label={t("masterdata.lw_details.ibanOption")}
              name="ibanOption"
            >
              <Select
                options={Object.keys(ibanOptionValues).map((key) => ({
                  label: t(
                    `masterdata.enums.IbanOptionTypeEnum.${ibanOptionValues[key]}`
                  ),
                  value: ibanOptionValues[key],
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={20}>
          <Col span={12}>
            <Button onClick={onClose} block>
              {t("discard")}
            </Button>
          </Col>
          <Col span={12}>
            <Button
              type="primary"
              htmlType="submit"
              loading={updateTHsResult.isLoading}
              block
            >
              {t("save_changes")}
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

export default UpdateTHsForm;
